import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Bold, Italic, Paperclip, Save, Underline, X } from 'lucide-react';

interface AddNoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave?: (data: { authorName: string; content: string; context?: string; attachments?: File[] }) => Promise<void> | void;
  currentUserName?: string | null;
  initialContent?: string;
  isDarkMode?: boolean;
}

function classNames(...values: Array<string | false | null | undefined>) {
  return values.filter(Boolean).join(' ');
}

export default function AddNoteModal({
  isOpen,
  onClose,
  onSave,
  currentUserName,
  initialContent = '',
  isDarkMode = false,
}: AddNoteModalProps) {
  const NOTE_CONTEXT_OPTIONS = useMemo(
    () => [
      { value: 'office-visit', label: 'Office visit' },
      { value: 'phone-call', label: 'Phone call' },
      { value: 'post-office-visit', label: 'Post office visit' },
      { value: 'home-care', label: 'At-home care observation' },
      { value: 'other', label: 'Other' },
    ],
    [],
  );

  const [guestName, setGuestName] = useState('');
  const [noteContent, setNoteContent] = useState(initialContent);
  const defaultContextValue = NOTE_CONTEXT_OPTIONS[0]?.value ?? '';
  const [noteContextValue, setNoteContextValue] = useState(defaultContextValue);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setNoteContent(initialContent);
  }, [initialContent]);

  useEffect(() => {
    if (!isOpen) {
      setGuestName('');
      setNoteContent(initialContent);
      setNoteContextValue(defaultContextValue);
      setAttachments([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [isOpen, initialContent, defaultContextValue]);

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== noteContent) {
      editorRef.current.innerHTML = noteContent;
    }
  }, [noteContent]);

  const authorName = useMemo(
    () => (currentUserName && currentUserName.trim().length > 0 ? currentUserName : guestName.trim()),
    [currentUserName, guestName],
  );

  if (!isOpen) {
    return null;
  }

  const handleFormat = (command: 'bold' | 'italic' | 'underline') => {
    if (typeof document === 'undefined') {
      return;
    }
    document.execCommand(command);
    if (editorRef.current) {
      setNoteContent(editorRef.current.innerHTML);
    }
  };

  const handleInput = () => {
    if (!editorRef.current) {
      return;
    }
    setNoteContent(editorRef.current.innerHTML);
  };

  const handleSelectAttachments = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    if (files.length > 0) {
      setAttachments(prev => [...prev, ...files]);
    }
    event.target.value = '';
  };

  const handleAttachmentButtonClick = () => {
    if (isSaving) {
      return;
    }
    fileInputRef.current?.click();
  };

  const handleSave = async () => {
    const trimmedContent = noteContent.trim();
    if (!authorName || (trimmedContent.length === 0 && attachments.length === 0) || isSaving) {
      return;
    }

    try {
      setIsSaving(true);
      if (onSave) {
        const contextLabel =
          NOTE_CONTEXT_OPTIONS.find(option => option.value === noteContextValue)?.label?.trim() ?? '';
        await onSave({
          authorName,
          content: trimmedContent,
          context: contextLabel || undefined,
          attachments,
        });
      }
      onClose();
    } catch (error) {
      console.error('Failed to save note', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      className={classNames(
        'fixed inset-0 z-50 flex items-center justify-center px-4 py-8 backdrop-blur-sm',
        isDarkMode ? 'bg-slate-100/70' : 'bg-slate-900/70',
      )}
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-note-title"
      onMouseDown={event => {
        if (!isSaving && event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className={classNames(
          'w-full max-w-4xl rounded-xl border shadow-2xl',
          isDarkMode
            ? 'border-slate-700 bg-slate-900 text-slate-100'
            : 'border-slate-200 bg-white text-slate-900',
        )}
        onMouseDown={event => {
          event.stopPropagation();
        }}
      >
        <div
          className={classNames(
            'flex items-center justify-between border-b px-6 py-4',
            isDarkMode ? 'border-slate-700' : 'border-slate-200',
          )}
        >
          <div className="flex flex-col gap-1">
            <h2 id="add-note-title" className="text-lg font-semibold">
              Add Note
            </h2>
          </div>
          <button
            type="button"
            onClick={isSaving ? undefined : onClose}
            className={classNames(
              'rounded-full p-2 transition',
              isDarkMode ? 'text-slate-300 hover:bg-slate-800' : 'text-slate-500 hover:bg-slate-100',
              isSaving ? 'cursor-not-allowed opacity-60' : undefined,
            )}
            aria-label="Close add note modal"
            disabled={isSaving}
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <div className="flex flex-col gap-4 px-6 py-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <input
              id="guest-name"
              type="text"
              value={currentUserName ?? guestName}
              onChange={event => {
                if (currentUserName) {
                  return;
                }
                setGuestName(event.target.value);
              }}
              placeholder="Enter your complete name, including titles if applicable (e.g., Dr. James Stafford)"
              className={classNames(
                'w-full rounded-md border px-3 py-3 text-sm outline-none transition focus:ring-2 focus:ring-blue-500 sm:w-2/3',
                isDarkMode
                  ? 'border-slate-700 bg-slate-800 text-slate-100 placeholder:text-slate-500'
                  : 'border-slate-300 bg-white text-slate-900 placeholder:text-slate-400',
                currentUserName ? 'cursor-not-allowed opacity-75 focus:ring-0' : undefined,
              )}
              aria-label="Your name"
              disabled={Boolean(currentUserName)}
              readOnly={Boolean(currentUserName)}
            />
            <select
              value={noteContextValue}
              onChange={event => setNoteContextValue(event.target.value)}
              className={classNames(
                'w-full rounded-md border px-3 py-3 text-sm outline-none transition focus:ring-2 focus:ring-blue-500 sm:w-1/3',
                isDarkMode
                  ? 'border-slate-700 bg-slate-800 text-slate-100'
                  : 'border-slate-300 bg-white text-slate-900',
              )}
              aria-label="When is this note being left?"
            >
              <option value="" />
              {NOTE_CONTEXT_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div
            className={classNames(
              'overflow-hidden rounded-lg border transition focus-within:border-blue-500 focus-within:ring-2',
              isDarkMode
                ? 'border-slate-700 bg-slate-900/70 focus-within:ring-blue-400/60'
                : 'border-slate-200 bg-white focus-within:ring-blue-500/40',
            )}
          >
            <div
              className={classNames(
                'flex items-center gap-2 border-b px-3 py-2 text-sm',
                isDarkMode
                  ? 'border-slate-700 bg-slate-900 text-slate-200'
                  : 'border-slate-200 bg-slate-50 text-slate-700',
              )}
            >
              <span className="text-xs font-semibold uppercase tracking-wide">Formatting</span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => handleFormat('bold')}
                  className={classNames(
                    'rounded-md p-2 transition',
                    isDarkMode ? 'hover:bg-slate-800' : 'hover:bg-slate-200',
                  )}
                  aria-label="Bold"
                >
                  <Bold className="h-4 w-4" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  onClick={() => handleFormat('italic')}
                  className={classNames(
                    'rounded-md p-2 transition',
                    isDarkMode ? 'hover:bg-slate-800' : 'hover:bg-slate-200',
                  )}
                  aria-label="Italic"
                >
                  <Italic className="h-4 w-4" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  onClick={() => handleFormat('underline')}
                  className={classNames(
                    'rounded-md p-2 transition',
                    isDarkMode ? 'hover:bg-slate-800' : 'hover:bg-slate-200',
                  )}
                  aria-label="Underline"
                >
                  <Underline className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            </div>

            <div
              ref={editorRef}
              contentEditable
              onInput={handleInput}
              onKeyDown={event => {
                if (event.key === 'Escape') {
                  event.preventDefault();
                  onClose();
                }
              }}
              className={classNames(
                'relative min-h-[200px] rounded-b-lg px-4 py-3 text-base outline-none transition',
                isDarkMode ? 'bg-slate-900 text-slate-100' : 'bg-white text-slate-900',
                noteContent.trim().length === 0
                  ? 'before:pointer-events-none before:absolute before:left-4 before:top-3 before:text-base before:text-slate-400 before:content-[attr(data-placeholder)]'
                  : undefined,
              )}
              data-placeholder="Record follow-up instructions, observations, or after-visit summary here…"
              suppressContentEditableWarning
          >
            {/* content handled via contentEditable; placeholder managed via pseudo-element */}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-4 px-6 pb-4">
        <div
          className={classNames(
            'w-full flex flex-col gap-3 rounded-lg border px-4 py-3 text-sm transition',
            attachments.length === 0
              ? isDarkMode
                ? 'border-dashed border-slate-700 bg-slate-900 text-slate-200'
                : 'border-dashed border-slate-200 bg-white text-slate-700'
              : isDarkMode
                ? 'border-slate-700 bg-slate-900 text-slate-100'
                : 'border-slate-200 bg-white text-slate-700',
          )}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleSelectAttachments}
            className="hidden"
          />
          <button
            type="button"
            onClick={handleAttachmentButtonClick}
            className={classNames(
              'inline-flex w-fit items-center gap-2 font-semibold transition',
              isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-500',
              isSaving ? 'cursor-not-allowed opacity-60 hover:text-current' : undefined,
            )}
            disabled={isSaving}
          >
            <Paperclip className="h-4 w-4" aria-hidden="true" />
            Add attachments
          </button>

          {attachments.length > 0 && (
            <ul className="flex flex-wrap gap-2 text-xs">
              {attachments.map((file, index) => (
                <li
                  key={`${file.name}-${index}`}
                  className={classNames(
                    'inline-flex items-center gap-2 rounded-full border px-3 py-1 pr-1',
                    isDarkMode
                      ? 'border-slate-700 bg-slate-800 text-slate-200'
                      : 'border-slate-200 bg-white text-slate-700',
                  )}
                >
                  <span className="max-w-[200px] truncate">{file.name}</span>
                  <button
                    type="button"
                    onClick={() => {
                      setAttachments(prev => prev.filter((_, fileIndex) => fileIndex !== index));
                    }}
                    className={classNames(
                      'rounded-full p-1 transition',
                      isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-200',
                    )}
                    aria-label={`Remove attachment ${file.name}`}
                  >
                    <X className="h-3 w-3" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div
        className={classNames(
          'flex items-center justify-end gap-3 border-t px-6 py-4',
          isDarkMode ? 'border-slate-700' : 'border-slate-200',
        )}
      >
        <button
          type="button"
          onClick={isSaving ? undefined : onClose}
          className={classNames(
            'rounded-full border px-4 py-2 text-sm font-semibold transition',
            isDarkMode
              ? 'border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800'
              : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-100',
            isSaving ? 'cursor-not-allowed opacity-60 hover:bg-transparent' : undefined,
          )}
          disabled={isSaving}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          className={classNames(
            'inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white transition',
            isDarkMode
              ? 'bg-blue-500 hover:bg-blue-600 disabled:bg-blue-500/40'
              : 'bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400/60',
            isSaving ? 'cursor-not-allowed' : undefined,
          )}
          disabled={!authorName || (noteContent.trim().length === 0 && attachments.length === 0) || isSaving}
        >
          <Save className="h-4 w-4" aria-hidden="true" />
          {isSaving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  </div>
);
}
